<template>
  <b-sidebar
    :visible="visible"
    no-header-close
    right
    backdrop
    shadow
    width="480px"
    @change="$emit('change', $event)"
  >
    <template #header>
      <b-row align-v="center" class="w-100 mt-2">
        <b-col cols="2">
          <button
            type="button"
            class="close"
            aria-label="Fechar"
            @click.prevent="close"
          >
            <span class="no no-e-remove"></span>
          </button>
        </b-col>
      </b-row>
    </template>

    <div class="pr-4 pl-4 pb-5">
      <h2 class="mensageria-send-panel__title mb-3">Painel de envio</h2>

      <label class="form-control-label d-block mb-1">
        Modelo para disparo
      </label>
      <b-form-select
        v-model="selectedModelKey"
        :options="modelSelectOptions"
        :disabled="loading || !models.length || submitting"
        class="mensageria-send-panel__model-select"
      />

      <p v-if="loading" class="text-muted small mt-2 mb-0">
        <i class="fas fa-spinner fa-spin mr-1"></i>
        Carregando modelos...
      </p>
      <p v-else-if="error" class="text-danger small mt-2 mb-0">
        {{ error }}
      </p>
      <p v-else-if="!models.length" class="text-muted small mt-2 mb-0">
        Nenhum modelo (<code>notifications.status = 3</code>) disponível.
      </p>

      <template v-if="selectedModel">
        <hr class="my-4" />

        <h3 class="mensageria-send-panel__form-title mb-3">
          {{ selectedModelTitle }}
          <span
            v-if="unmappedVars.length"
            class="mensageria-send-panel__unmapped-warn"
            :title="unmappedVarsTitle"
            aria-label="campos não mapeados"
          >
            <i class="fa fa-exclamation-triangle" aria-hidden="true"></i>
            campos não mapeados
          </span>
        </h3>

        <div v-if="!formFields.length" class="text-muted small mb-3">
          Este modelo não tem campos em
          <code>message.message_data</code>.
        </div>

        <form class="mensageria-send-panel__form" @submit.prevent="enviar">
          <div
            v-for="field in formFields"
            :key="field.slug"
            class="form-group mb-3"
          >
            <label
              class="form-control-label d-block mb-1"
              :for="`send-field-${field.slug}`"
            >
              <span class="mensageria-send-panel__label">{{
                field.label
              }}</span>
            </label>
            <input
              :id="`send-field-${field.slug}`"
              v-model="formValues[field.slug]"
              type="text"
              class="form-control"
              :placeholder="field.slug"
              :disabled="submitting"
              autocomplete="off"
            />
          </div>

          <div class="form-group mb-3">
            <label class="form-control-label d-block mb-1" for="send-to">
              Destinatário (to)
            </label>
            <input
              id="send-to"
              v-model="to"
              type="text"
              class="form-control"
              placeholder="telefone, e-mail ou destino"
              :disabled="submitting"
              autocomplete="off"
            />
          </div>

          <b-row>
            <b-col cols="12" md="6" class="mb-3">
              <label class="form-control-label d-block mb-1">
                Data (send_date)
              </label>
              <date-picker
                v-model="sendDatePart"
                type="date"
                format="dd/MM/yyyy"
                value-format="yyyy-MM-dd"
                :clearable="false"
                :disabled="submitting"
                class="w-100"
              />
            </b-col>
            <b-col cols="12" md="6" class="mb-3">
              <label class="form-control-label d-block mb-1">Hora</label>
              <time-picker
                v-model="sendTimePart"
                format="HH:mm"
                value-format="HH:mm:ss"
                placeholder="HH:mm"
                :clearable="false"
                :disabled="submitting"
                class="w-100 mensageria-send-panel__time-picker"
              />
            </b-col>
          </b-row>

          <div class="form-group mb-3">
            <label
              class="form-control-label d-block mb-1"
              for="send-message-preview"
            >
              Pré-visualização da mensagem
            </label>
            <div
              id="send-message-preview"
              class="mensageria-send-panel__preview"
              role="textbox"
              aria-readonly="true"
              tabindex="0"
            >
              <pre v-if="messagePreview" class="mensageria-send-panel__preview-text mb-0">{{
                messagePreview
              }}</pre>
              <span v-else class="text-muted small"
                >Sem conteúdo em <code>model_message</code>.</span
              >
            </div>
          </div>

          <p v-if="submitError" class="text-danger small mb-2">
            {{ submitError }}
          </p>

          <button
            type="submit"
            class="btn btn-primary"
            :disabled="!canSubmit || submitting"
          >
            <i v-if="submitting" class="fas fa-spinner fa-spin mr-1"></i>
            Enviar
          </button>
        </form>
      </template>
    </div>
  </b-sidebar>
</template>

<script>
import moment from 'moment'
import { DatePicker, TimePicker } from 'element-ui'
import nodeService from '@/services/nodejs'
import {
  listMessageDataFields,
  buildDispatchMessage,
  buildDispatchTitle,
  buildMessagePreview,
  findUnmappedModelMessageVars,
  resolveModelChannel,
} from './utils/messageData'

export default {
  name: 'MensageriaSendPanel',
  components: {
    DatePicker,
    TimePicker,
  },
  props: {
    visible: { type: Boolean, default: false },
    /** AdminModelItem[] de GET /messenger/admin/models */
    models: { type: Array, default: () => [] },
    loading: { type: Boolean, default: false },
    error: { type: String, default: '' },
  },
  data() {
    return {
      /** one model only — uuid or id string */
      selectedModelKey: null,
      /** slug → valor digitado para disparo */
      formValues: {},
      to: '',
      sendDatePart: moment().format('YYYY-MM-DD'),
      sendTimePart: moment().format('HH:mm:ss'),
      submitting: false,
      submitError: '',
    }
  },
  computed: {
    modelSelectOptions() {
      return [
        {
          value: null,
          text: 'Selecionar modelo para disparo',
          disabled: true,
        },
        ...this.models.map((model) => ({
          value: this.modelKey(model),
          text: this.modelTitle(model),
        })),
      ]
    },
    selectedModel() {
      if (!this.selectedModelKey) return null
      return (
        this.models.find(
          (model) => this.modelKey(model) === this.selectedModelKey
        ) || null
      )
    },
    selectedModelTitle() {
      return this.modelTitle(this.selectedModel)
    },
    formFields() {
      return listMessageDataFields(this.selectedModel)
    },
    unmappedVars() {
      return findUnmappedModelMessageVars(this.selectedModel)
    },
    unmappedVarsTitle() {
      if (!this.unmappedVars.length) return ''
      return (
        'Variáveis em model_message sem entrada em message_data: ' +
        this.unmappedVars.join(', ')
      )
    },
    messagePreview() {
      return buildMessagePreview(this.selectedModel, this.formValues)
    },
    sendDate() {
      if (!this.sendDatePart || !this.sendTimePart) return ''
      return `${this.sendDatePart} ${this.sendTimePart}`
    },
    canSubmit() {
      return (
        !!this.selectedModel &&
        !!String(this.to || '').trim() &&
        !!this.sendDate &&
        !!resolveModelChannel(this.selectedModel)
      )
    },
  },
  watch: {
    visible(isOpen) {
      if (isOpen) {
        this.syncSelectionWithModels()
        this.submitError = ''
      }
    },
    models() {
      this.syncSelectionWithModels()
    },
    selectedModelKey() {
      this.resetFormValues()
      this.submitError = ''
    },
    formFields: {
      handler() {
        this.ensureFormValueKeys()
      },
      deep: true,
    },
  },
  methods: {
    modelKey(model) {
      if (!model) return null
      if (model.uuid) return String(model.uuid)
      if (model.id != null) return String(model.id)
      return null
    },
    modelTitle(model) {
      return (
        (model &&
          (model.title || model.modelName || model.model_name || model.uuid)) ||
        'Modelo'
      )
    },
    resetFormValues() {
      const next = {}
      this.formFields.forEach((field) => {
        next[field.slug] = ''
      })
      this.formValues = next
    },
    ensureFormValueKeys() {
      const next = { ...this.formValues }
      let changed = false
      this.formFields.forEach((field) => {
        if (!Object.prototype.hasOwnProperty.call(next, field.slug)) {
          next[field.slug] = ''
          changed = true
        }
      })
      if (changed) this.formValues = next
    },
    syncSelectionWithModels() {
      if (!this.selectedModelKey) return
      const stillThere = this.models.some(
        (model) => this.modelKey(model) === this.selectedModelKey
      )
      if (!stillThere) {
        this.selectedModelKey = null
        this.formValues = {}
      }
    },
    buildPayload() {
      const model = this.selectedModel
      if (!model) return null

      const to = String(this.to || '').trim()
      const send_date = this.sendDate
      const channel = resolveModelChannel(model)
      const title = buildDispatchTitle(model, this.formValues)
      const message = buildDispatchMessage(model, this.formValues, {
        to,
        sendDate: send_date,
      })

      const payload = {
        title,
        message,
        send_date,
        to,
        channel,
      }

      if (model.uuid) {
        payload.model_uuid = model.uuid
      } else if (model.id != null) {
        payload.model_id = Number(model.id)
      }

      return payload
    },
    async enviar() {
      if (!this.canSubmit || this.submitting) return

      const payload = this.buildPayload()
      if (!payload || (!payload.model_uuid && payload.model_id == null)) {
        this.submitError = 'Selecione um modelo válido.'
        return
      }
      if (!payload.channel) {
        this.submitError = 'Modelo sem channel / model_channel.'
        return
      }

      this.submitting = true
      this.submitError = ''
      try {
        await nodeService.postMessengerAdminNotification(payload)
        if (typeof this.$notify === 'function') {
          this.$notify({
            type: 'success',
            message: 'Disparo agendado (status=2).',
          })
        }
        this.$emit('sent', payload)
        this.close()
      } catch (err) {
        const apiMsg =
          err &&
          err.response &&
          err.response.data &&
          (err.response.data.message || err.response.data.error)
        this.submitError =
          apiMsg || 'Não foi possível criar o disparo admin.'
        if (typeof this.$notify === 'function') {
          this.$notify({
            type: 'danger',
            message: this.submitError,
          })
        }
      } finally {
        this.submitting = false
      }
    },
    close() {
      this.$emit('change', false)
    },
  },
}
</script>

<style scoped>
.mensageria-send-panel__title {
  color: #12576e;
}

.mensageria-send-panel__form-title {
  color: #12576e;
  font-size: 1.15rem;
  font-weight: 600;
}

.mensageria-send-panel__model-select {
  width: 100%;
}

.mensageria-send-panel__slug {
  display: inline-block;
  margin-right: 0.5rem;
  color: #12576e;
  font-weight: 600;
}

.mensageria-send-panel__label {
  color: #495057;
  font-weight: 500;
}

.mensageria-send-panel__unmapped-warn {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  margin-left: 0.5rem;
  padding: 0.15rem 0.45rem;
  border-radius: 0.25rem;
  background: #fff8e6;
  color: #b78103;
  font-size: 0.8rem;
  font-weight: 600;
  vertical-align: middle;
}

.mensageria-send-panel__unmapped-warn .fa-exclamation-triangle {
  color: #e6a23c;
}

.mensageria-send-panel__preview {
  min-height: 6.5rem;
  max-height: 16rem;
  overflow: auto;
  padding: 0.75rem 0.9rem;
  border: 1px solid #ced4da;
  border-radius: 0.25rem;
  background: #f8f9fa;
  color: #212529;
}

.mensageria-send-panel__preview-text {
  white-space: pre-wrap;
  word-break: break-word;
  font-family: inherit;
  font-size: 0.9rem;
  line-height: 1.45;
}
</style>
